// Eén register van vakken en de opties per vak (15-10-2026, Yasar).
//
// Dit is de enige vakkenlijst. Wijzigen gaat via een PR en docs/BESLUITEN.md, en geldt
// voor alle scholen gelijk. De tabel in docs/VAKKEN.md is met de hand uit dit bestand
// overgenomen; bij verschil geldt de code.
//
// - VAKGROEPEN: per groep een naam en de opties (de matrix).
// - VAKKEN: per vak de groep, de voorleestaal, de aliassen (namen en Zermelo-/Magister-
//   codes) en eventueel opties die afwijken van de groep.
// - vakProfiel(naam) / optiesVoor(naam): een vrij getypte vaknaam herkennen.
//
// De algemene vraagsoorten (meerkeuze, open, invul, waar/onwaar, koppelen, ordenen,
// categoriseren) en media bij een vraag staan niet in de opties: die zijn er altijd.
//
// PR A: nog niets gebruikt dit register. Dat komt in PR B tot en met D.

/** Alle optiesleutels. Elke groep heeft ze allemaal, als boolean. */
export const OPTIE_SLEUTELS = Object.freeze([
  'getal',             // vraagsoort Getal
  'grafiek',           // vraagsoort Grafiek en grafiek in het mediamenu
  'formuleKnop',       // formuleknop in de vraageditor
  'symbolen',          // bèta-symbolentoetsenbord
  'leestekst',         // bron Leestekst
  'luisterfragment',   // bron Luisterfragment
  'toetssoortLees',    // toetssoort Leestoets
  'toetssoortLuister', // toetssoort Luistertoets
  'woordjes',          // woordjestoets + leerkaarten Woorden/Zinnen
  'begrippen',         // leerkaarten Begrippen
  'tekstAanklikken',   // vraagsoort Tekst aanklikken
  'accenten',          // accenttoetsenbord (é ç ß ñ) voor de leerling
  'bronnen',           // TOEKOMSTIG: bronvraag (bron als tekst, afbeelding, kaart of tabel met vragen erbij); nog geen UI
  'diagram',           // vraagsoort Diagram: staafdiagram/klimaatgrafiek (los van grafiek = coördinatenvlak)
  'formule',           // vraagsoort Formule: uitdrukking herleiden, nagekeken op gelijkwaardigheid (01-11-2026)
]);

const opties = (aan = []) => Object.freeze(
  Object.fromEntries(OPTIE_SLEUTELS.map((k) => [k, aan.includes(k)]))
);

const REKENEN = ['getal', 'grafiek', 'formuleKnop', 'symbolen', 'begrippen'];

/** Opties voor een onbekend of leeg vak: alleen de algemene vraagsoorten. */
export const BASIS_OPTIES = opties([]);

/** Volgorde = volgorde in de keuzelijst. */
export const VAKGROEPEN = Object.freeze({
  mvt: { naam: 'Moderne vreemde talen', opties: opties(['leestekst', 'luisterfragment', 'toetssoortLees', 'toetssoortLuister', 'woordjes', 'tekstAanklikken']) },
  nederlands: { naam: 'Nederlands', opties: opties(['leestekst', 'luisterfragment', 'toetssoortLees', 'toetssoortLuister', 'begrippen', 'tekstAanklikken']) },
  klassiek: { naam: 'Klassieke talen', opties: opties(['leestekst', 'toetssoortLees', 'woordjes', 'tekstAanklikken']) },
  // Formule (herleiden) voorlopig alleen bij wiskunde en rekenen (01-11-2026).
  wiskunde: { naam: 'Wiskunde en rekenen', opties: opties([...REKENEN, 'formule']) },
  beta: { naam: 'Bètavakken', opties: opties(REKENEN) },
  economie: { naam: 'Economie', opties: opties(REKENEN) },
  // Bronvraag vervangt tekst aanklikken bij Gs/Ak (15-10-2026, Yasar).
  mensmaatschappij: { naam: 'Mens en maatschappij', opties: opties(['begrippen', 'bronnen']) },
  kunst: { naam: 'Kunst en cultuur', opties: opties(['begrippen']) },
  overig: { naam: 'Overig', opties: opties([]) },
});

/**
 * Aliassen worden genormaliseerd vergeleken (zie normaliseerVak). Codes van twee
 * letters tellen alleen bij een exacte match. Alleen eenduidige codes; twijfelgevallen
 * (in, me, te, hv, sp, wc, wd) staan er bewust niet in.
 */
export const VAKKEN = Object.freeze([
  // Moderne vreemde talen
  { key: 'engels', naam: 'Engels', groep: 'mvt', taal: 'en', aliassen: ['engels', 'english', 'engelse', 'en', 'eng', 'entl', 'anglais', 'englisch'] },
  { key: 'frans', naam: 'Frans', groep: 'mvt', taal: 'fr', aliassen: ['frans', 'francais', 'french', 'franse', 'fa', 'fatl', 'französisch'], opties: { accenten: true } },
  { key: 'duits', naam: 'Duits', groep: 'mvt', taal: 'de', aliassen: ['duits', 'deutsch', 'german', 'duitse', 'du', 'dutl', 'allemand'], opties: { accenten: true } },
  { key: 'spaans', naam: 'Spaans', groep: 'mvt', taal: 'es', aliassen: ['spaans', 'espanol', 'spanish', 'spaanse', 'spa', 'sptl'], opties: { accenten: true } },
  // Nederlands
  { key: 'nederlands', naam: 'Nederlands', groep: 'nederlands', taal: 'nl', aliassen: ['nederlands', 'nederlandse', 'ne', 'ned', 'netl'] },
  // Klassieke talen
  { key: 'latijn', naam: 'Latijn', groep: 'klassiek', aliassen: ['latijn', 'la', 'lat', 'latijnse'] },
  { key: 'grieks', naam: 'Grieks', groep: 'klassiek', aliassen: ['grieks', 'gr', 'griekse'] },
  // Wiskunde en rekenen
  { key: 'wiskunde', naam: 'Wiskunde', groep: 'wiskunde', aliassen: ['wiskunde', 'wiskunde a', 'wiskunde b', 'wiskunde c', 'wiskunde d', 'wis', 'wi', 'wisk', 'wa', 'wb', 'wisa', 'wisb', 'wisc', 'wisd', 'math', 'maths', 'mathematics'] },
  { key: 'rekenen', naam: 'Rekenen', groep: 'wiskunde', aliassen: ['rekenen', 'rek'] },
  // Bètavakken
  { key: 'natuurkunde', naam: 'Natuurkunde', groep: 'beta', aliassen: ['natuurkunde', 'na', 'nat', 'physics'] },
  { key: 'scheikunde', naam: 'Scheikunde', groep: 'beta', aliassen: ['scheikunde', 'sk', 'chemie', 'chemistry'] },
  { key: 'nask', naam: 'NaSk', groep: 'beta', aliassen: ['nask', 'nask1', 'nask2', 'nask 1', 'nask 2', 'nsk', 'natuur- en scheikunde', 'natuur en scheikunde'] },
  { key: 'biologie', naam: 'Biologie', groep: 'beta', aliassen: ['biologie', 'bi', 'bio', 'biology'] },
  { key: 'nlt', naam: 'NLT', groep: 'beta', aliassen: ['nlt', 'natuur leven en technologie', 'natuur, leven en technologie'] },
  { key: 'techniek', naam: 'Techniek', groep: 'beta', aliassen: ['techniek', 'technologie'] },
  // Economie
  { key: 'economie', naam: 'Economie', groep: 'economie', aliassen: ['economie', 'ec', 'eco', 'economics'] },
  { key: 'bedrijfseconomie', naam: 'Bedrijfseconomie', groep: 'economie', aliassen: ['bedrijfseconomie', 'beco', 'm&o', 'management & organisatie', 'management en organisatie'] },
  // Mens en maatschappij
  { key: 'geschiedenis', naam: 'Geschiedenis', groep: 'mensmaatschappij', aliassen: ['geschiedenis', 'gs', 'ges', 'history'] },
  { key: 'aardrijkskunde', naam: 'Aardrijkskunde', groep: 'mensmaatschappij', aliassen: ['aardrijkskunde', 'ak', 'geography'], opties: { diagram: true } },
  { key: 'maatschappijleer', naam: 'Maatschappijleer', groep: 'mensmaatschappij', aliassen: ['maatschappijleer', 'maatschappijkunde', 'ma'] },
  { key: 'maatschappijwetenschappen', naam: 'Maatschappijwetenschappen', groep: 'mensmaatschappij', aliassen: ['maatschappijwetenschappen', 'maw'] },
  { key: 'levensbeschouwing', naam: 'Levensbeschouwing', groep: 'mensmaatschappij', aliassen: ['levensbeschouwing', 'godsdienst', 'gd', 'religie'] },
  { key: 'filosofie', naam: 'Filosofie', groep: 'mensmaatschappij', aliassen: ['filosofie', 'philosophy'] },
  // Kunst en cultuur
  { key: 'kunst', naam: 'Kunst', groep: 'kunst', aliassen: ['kunst', 'kunstvakken', 'kunst algemeen', 'beeldende vorming', 'bv'] },
  { key: 'ckv', naam: 'CKV', groep: 'kunst', aliassen: ['ckv', 'culturele en kunstzinnige vorming'] },
  { key: 'muziek', naam: 'Muziek', groep: 'kunst', aliassen: ['muziek', 'mu', 'music'] },
  { key: 'tekenen', naam: 'Tekenen', groep: 'kunst', aliassen: ['tekenen'] },
  { key: 'handvaardigheid', naam: 'Handvaardigheid', groep: 'kunst', aliassen: ['handvaardigheid'] },
  { key: 'drama', naam: 'Drama', groep: 'kunst', aliassen: ['drama'] },
  // Overig. Informatica wordt later helemaal anders ingericht met eigen code van
  // Mohamed; voor Fiducie nu niet nodig (15-10-2026, Yasar).
  { key: 'informatica', naam: 'Informatica', groep: 'overig', aliassen: ['informatica', 'inf', 'info'] },
  { key: 'lo', naam: 'Lichamelijke opvoeding', groep: 'overig', aliassen: ['lichamelijke opvoeding', 'lo', 'gym', 'gymnastiek', 'bewegingsonderwijs'] },
  { key: 'bsm', naam: 'BSM', groep: 'overig', aliassen: ['bsm', 'bewegen sport en maatschappij', 'bewegen, sport en maatschappij'] },
  { key: 'mentoruur', naam: 'Mentoruur', groep: 'overig', aliassen: ['mentoruur', 'mentor', 'mentorles'] },
].map((v) => Object.freeze(v)));

// Woorden aan het eind die niets zeggen over het vak ("Engels tto", "Nederlandse taal").
const LOZE_STAARTWOORDEN = new Set(['taal', 'tto', 'versterkt']);

/**
 * Vaknaam → vergelijkbare tekst: trim, kleine letters, accenten weg, toevoegingen
 * tussen haakjes eraf, spaties samen, en "taal"/"tto"/"versterkt" aan het eind weg.
 */
export function normaliseerVak(naam) {
  let s = String(naam == null ? '' : naam)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  let woorden = s ? s.split(' ') : [];
  while (woorden.length > 1 && LOZE_STAARTWOORDEN.has(woorden[woorden.length - 1])) {
    woorden = woorden.slice(0, -1);
  }
  return woorden.join(' ');
}

// Profielen en alias-index één keer opbouwen.
const PROFIELEN = new Map();
const ALIAS_NAAR_KEY = new Map();
VAKKEN.forEach((v) => {
  const groep = VAKGROEPEN[v.groep];
  PROFIELEN.set(v.key, Object.freeze({
    key: v.key,
    naam: v.naam,
    groep: v.groep,
    taal: v.taal || 'nl',
    opties: Object.freeze({ ...(groep ? groep.opties : BASIS_OPTIES), ...(v.opties || {}) }),
  }));
  v.aliassen.forEach((a) => {
    const n = normaliseerVak(a);
    if (n && !ALIAS_NAAR_KEY.has(n)) ALIAS_NAAR_KEY.set(n, v.key);
  });
});
// Langste alias eerst, voor de prefix-match.
const PREFIX_ALIASSEN = [...ALIAS_NAAR_KEY.keys()]
  .filter((a) => a.length >= 3)
  .sort((a, b) => b.length - a.length);

// Een ander vak verderop in de naam ("engelstalige economie"): een reeks hele woorden
// na het eerste woord die precies een alias van minstens 4 tekens is.
function vakVerderopInNaam(woorden) {
  for (let i = 1; i < woorden.length; i += 1) {
    for (let j = woorden.length; j > i; j -= 1) {
      const stuk = woorden.slice(i, j).join(' ');
      if (stuk.length >= 4 && ALIAS_NAAR_KEY.has(stuk)) return ALIAS_NAAR_KEY.get(stuk);
    }
  }
  return null;
}

/**
 * Vaknaam (vrije tekst of rooster-code) → { key, naam, groep, taal, opties } of null.
 * 1. exacte alias; 2. prefix op een alias van minstens 3 tekens (langste wint), maar
 * alleen als verderop in de naam geen ander vak staat; staat dat er wel, dan dat vak.
 */
export function vakProfiel(naam) {
  const n = normaliseerVak(naam);
  if (!n) return null;
  const exact = ALIAS_NAAR_KEY.get(n);
  if (exact) return PROFIELEN.get(exact);
  const prefix = PREFIX_ALIASSEN.find((a) => n.startsWith(a));
  if (!prefix) return null;
  const key = ALIAS_NAAR_KEY.get(prefix);
  const ander = vakVerderopInNaam(n.split(' '));
  if (ander && ander !== key) return PROFIELEN.get(ander);
  return PROFIELEN.get(key);
}

/** Opties voor een vak; een leeg of onbekend vak krijgt de basisset. */
export function optiesVoor(naam) {
  const profiel = vakProfiel(naam);
  return profiel ? profiel.opties : BASIS_OPTIES;
}

/** Vakken voor een keuzelijst: per groep (volgorde van VAKGROEPEN), daarbinnen op naam. */
export function vakkenVoorKeuze() {
  const groepen = Object.keys(VAKGROEPEN);
  return [...VAKKEN]
    .sort((a, b) => (groepen.indexOf(a.groep) - groepen.indexOf(b.groep)) || a.naam.localeCompare(b.naam, 'nl'))
    .map((v) => ({ key: v.key, naam: v.naam, groep: v.groep }));
}
